import { InputAdornment, TextField as MuiTextField, Theme, makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import React from 'react';

export interface TextFieldProps {
  currency?: boolean;
  percentage?: boolean;
  characterLimit?: number;
  showCharacterLimit?: boolean;
  isMultiline?: boolean;
  numRows?: number;
  error?: string;
  touched?: boolean;
  onChange: (e: any) => void;
  onBlur?: (e: any) => void;
  onFocus?: (e: any) => void;
  value?: string | number;
  name?: string;
  label: string;
  placeholder?: string;
  type?: string;
  required?: boolean;
  disabled?: boolean;
}

export default function TextField({
  currency,
  percentage,
  characterLimit,
  showCharacterLimit,
  name,
  placeholder,
  type,
  isMultiline,
  numRows,
  error,
  touched,
  onChange,
  value,
  onBlur,
  ...rest
}: TextFieldProps) {
  const classes = useStyles();

  const errorAndTouched = touched && !!error;

  const isFloat = type === 'float';
  const isSignedFloat = type === 'signedFloat';
  if (isFloat || isSignedFloat) {
    type = 'number';
  }

  const handleDecimal = (value: string) => {
    if (value.indexOf('.') != -1) {
      if (value.split('.')[1].length <= 2) onChange(value.trim());
      else {
        const floatValue = value.split('.');
        onChange(floatValue[0] + '.' + floatValue[1].substring(0, 2));
      }
    } else onChange(value.trim());
  };

  const onFieldBlur = (e: any) => {
    onBlur?.(e);
    if (currency && e.target.value !== '') {
      onChange((Math.round(e.target.value * 100) / 100).toFixed(2));
    }
  };

  return (
    <>
      <MuiTextField
        {...rest}
        data-testid={name}
        value={value}
        error={errorAndTouched}
        helperText={errorAndTouched ? error : ''}
        variant="filled"
        fullWidth
        InputLabelProps={{
          className: classes.label,
          shrink: true,
          required: false,
        }}
        label={
          rest.required ? (
            <>
              {rest.label} <span style={{ color: 'red' }}>*</span>
            </>
          ) : (
            rest.label
          )
        }
        InputProps={{
          className: classNames(errorAndTouched ? classes.errorField : classes.textField, {
            [classes.multilineRoot]: isMultiline,
          }),
          startAdornment: currency ? <InputAdornment position="start">$</InputAdornment> : undefined,
          endAdornment: percentage ? <InputAdornment position="end">%</InputAdornment> : undefined,
          disableUnderline: true,
        }}
        inputProps={{
          maxLength: characterLimit,
          className: classNames({
            [classes.textAreaInput]: isMultiline,
            [classes.innerInput]: !isMultiline && !currency,
          }),
          onWheel: (e: React.WheelEvent<HTMLInputElement>) => {
            if (type === 'number' || currency) {
              (e.target as HTMLInputElement).blur();
            }
          },
        }}
        onKeyDown={(e) => currency && e.keyCode === 69 && e.preventDefault()}
        placeholder={placeholder}
        type={currency ? 'number' : type}
        style={{
          marginBottom: errorAndTouched ? 0 : 22,
        }}
        multiline={isMultiline ?? false}
        rows={numRows}
        onBlur={onFieldBlur}
        onChange={(event) => {
          const value = event.target.value;

          if (type === 'number' && !(isFloat || isSignedFloat)) {
            const number = parseInt(value);
            if ((number || number === 0) && !isNaN(number) && Math.sign(number) !== -1) {
              onChange(number);
            } else {
              onChange('');
            }
          } else if (type === 'number' && (isFloat || isSignedFloat)) {
            const number = parseFloat(value);
            if (Math.sign(number) === -1 && isSignedFloat) {
              handleDecimal(value);

              return;
            } else if (Math.sign(number) === -1) {
              return;
            }
            if ((number || number == 0) && !isNaN(number) && Math.sign(number) !== -1) {
              handleDecimal(value);
            } else {
              onChange('');
            }
          } else {
            onChange(value);
          }
        }}
      />
      {characterLimit && showCharacterLimit && (
        <div
          className={classNames({
            [classes.characterLimitContainer]: true,
            [classes.empty]: (value as string)?.length === 0,
            [classes.valid]: (value as string)?.length > 0 && (value as string)?.length <= characterLimit,
            [classes.invalid]: (value as string)?.length > characterLimit,
          })}
        >
          <span>
            {(value as string)?.length ? (value as string).length : 0}/{characterLimit}
          </span>
        </div>
      )}
    </>
  );
}

const useStyles = makeStyles((theme: Theme) => ({
  textField: {
    marginTop: 0,
    marginBottom: 0,
    minHeight: 80,
    backgroundColor: 'transparent',
    width: '100%',
    borderWidth: '1px',
    borderColor: '#E0E0E0',
    borderStyle: 'solid',
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
    '&:focus-within': {
      borderColor: theme.palette.primary.main,
    },
    borderRadius: 4,
    '&.Mui-disabled': {
      backgroundColor: 'hsl(0, 0%, 95%)',
    },
  },
  errorField: {
    borderColor: 'red',
    marginTop: 0,
    marginBottom: 0,
    minHeight: 80,
    backgroundColor: 'transparent',
    width: '100%',
    borderWidth: '1px',
    borderStyle: 'solid',
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
    '&:focus-within': {
      borderColor: theme.palette.primary.main,
    },
    borderRadius: 4,
    '&.Mui-disabled': {
      backgroundColor: 'hsl(0, 0%, 95%)',
    },
  },
  label: {
    fontSize: 16,
    padding: 16,
    transform: 'none !important',
  },
  innerInput: {
    padding: '27px 16px 10px',
  },
  multilineRoot: {
    paddingTop: 37,
  },
  textAreaInput: {
    padding: '0 4px 10px',
  },
  characterLimitContainer: {
    display: 'flex',
    flexDirection: 'row' as const,
    justifyContent: 'flex-end',
    fontSize: '12px',
    marginTop: '-20px',
    height: 20,
  },
  empty: {
    color: 'rgba(0, 0, 0, 0.6)',
  },
  valid: {
    color: '#4471B8',
  },
  invalid: {
    color: '#F44336',
  },
}));
